/**
 * svgRenderer.ts
 * Shared imperative SVG rendering engine for the SEG Frame Builder.
 * Used by both VisualizerCanvas.vue (interactive) and PrintFrameSvg.vue (print).
 */
import { THEME, HD, HARDWARE_LEGEND, HARDWARE_LABELS, ASSEMBLY_STEP_TYPES, FRAME_COLORS } from './frameConstants';
// ── SVG ELEMENT FACTORY ──────────────────────────────────────
const NS = 'http://www.w3.org/2000/svg';
function svgEl(tag, attrs = {}, text = null) {
    const el = document.createElementNS(NS, tag);
    for (const [k, v] of Object.entries(attrs))
        el.setAttribute(k, String(v));
    if (text !== null)
        el.textContent = text;
    return el;
}
const mkG = (attrs = {}) => svgEl('g', attrs);
const mkRect = (attrs) => svgEl('rect', attrs);
const mkCircle = (attrs) => svgEl('circle', attrs);
const mkLine = (attrs) => svgEl('line', attrs);
const mkPath = (attrs) => svgEl('path', attrs);
const mkText = (attrs, txt) => svgEl('text', attrs, txt);
const mkPolygon = (attrs) => svgEl('polygon', attrs);
// ── DROP-SHADOW FILTER ───────────────────────────────────────
function makeShadowDef(id, scale) {
    const defs = svgEl('defs');
    const filter = svgEl('filter', { id, x: '-20%', y: '-20%', width: '140%', height: '140%' });
    filter.appendChild(svgEl('feDropShadow', {
        dx: 2 * scale, dy: 2 * scale, stdDeviation: 2 * scale,
        'flood-color': '#000', 'flood-opacity': '0.2',
    }));
    defs.appendChild(filter);
    return defs;
}
// ── DIMENSION ANNOTATION ─────────────────────────────────────
function renderDimension(x1, y1, x2, y2, label, offset, isVert, fontSize) {
    const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
    const ox = isVert ? offset : 0, oy = isVert ? 0 : offset;
    const tw = label.length * (fontSize * 0.6) + 10;
    const gr = mkG();
    gr.appendChild(mkLine({ x1, y1, x2: x1 + ox, y2: y1 + oy, stroke: '#999', 'stroke-width': 1, 'vector-effect': 'non-scaling-stroke' }));
    gr.appendChild(mkLine({ x1: x2, y1: y2, x2: x2 + ox, y2: y2 + oy, stroke: '#999', 'stroke-width': 1, 'vector-effect': 'non-scaling-stroke' }));
    gr.appendChild(mkLine({ x1: x1 + ox, y1: y1 + oy, x2: x2 + ox, y2: y2 + oy, stroke: '#333', 'stroke-width': 1, 'vector-effect': 'non-scaling-stroke' }));
    gr.appendChild(mkCircle({ cx: x1 + ox, cy: y1 + oy, r: 2, fill: '#333' }));
    gr.appendChild(mkCircle({ cx: x2 + ox, cy: y2 + oy, r: 2, fill: '#333' }));
    gr.appendChild(mkRect({
        x: mx + ox - tw / 2, y: my + oy - fontSize * .9, width: tw, height: fontSize * 1.8,
        fill: 'white', stroke: '#333', 'stroke-width': 1, 'vector-effect': 'non-scaling-stroke',
    }));
    gr.appendChild(mkText({
        x: mx + ox, y: my + oy + fontSize * .35,
        'text-anchor': 'middle', 'font-size': fontSize, fill: '#000', 'font-weight': 'bold',
    }, label));
    return gr;
}
// ── FRAME SEGMENT (mitered rail) ─────────────────────────────
function renderFrameSeg(tx, ty, w, h, type, fill, stroke, strokeW, filterId) {
    const tl = { x: 0, y: 0 }, tr = { x: w, y: 0 }, br = { x: w, y: h }, bl = { x: 0, y: h };
    switch (type) {
        case 'top-start':
            bl.x += h;
            break;
        case 'top-end':
            br.x -= h;
            break;
        case 'top-full':
            bl.x += h;
            br.x -= h;
            break;
        case 'bot-start':
            tl.x += h;
            break;
        case 'bot-end':
            tr.x -= h;
            break;
        case 'bot-full':
            tl.x += h;
            tr.x -= h;
            break;
        case 'left-start':
            tr.y += w;
            break;
        case 'left-end':
            br.y -= w;
            break;
        case 'left-full':
            tr.y += w;
            br.y -= w;
            break;
        case 'right-start':
            tl.y += w;
            break;
        case 'right-end':
            bl.y -= w;
            break;
        case 'right-full':
            tl.y += w;
            bl.y -= w;
            break;
    }
    const p = mkPath({
        d: `M${tl.x},${tl.y} L${tr.x},${tr.y} L${br.x},${br.y} L${bl.x},${bl.y} Z`,
        fill, stroke, 'stroke-width': strokeW,
        'vector-effect': 'non-scaling-stroke',
        transform: `translate(${tx},${ty})`,
    });
    if (filterId)
        p.setAttribute('filter', `url(#${filterId})`);
    return p;
}
// ── SCREW / ANCHOR ──────────────────────────────────────────
function renderScrew(cx, cy) {
    const style = getHardwareStyle(HARDWARE_LABELS.SCREW_ANCHOR);
    return mkCircle({
        cx, cy, r: 0.5,
        fill: style.color,
        stroke: style.border,
        'stroke-width': 0.5,
        'vector-effect': 'non-scaling-stroke'
    });
}
// ── SPLICE CONNECTOR ─────────────────────────────────────────
function renderSplice(x, y, w, h, highlight, vertical, showScrews = true) {
    const style = getHardwareStyle(HARDWARE_LABELS.SPLICE_CONNECTOR);
    const gr = mkG();
    gr.appendChild(mkRect({
        x, y, width: w, height: h,
        fill: highlight ? THEME.danger : style.color,
        stroke: HD.aluminumDark, 'stroke-width': 1,
        'vector-effect': 'non-scaling-stroke', rx: 0.1,
    }));
    if (showScrews) {
        const screws = vertical
            ? [1, 2, 3, 4].map(i => ({ cx: x + w * .5, cy: y + (h / 5) * i }))
            : [1, 2, 3, 4].map(i => ({ cx: x + (w / 5) * i, cy: y + h * .5 }));
        screws.forEach(s => gr.appendChild(renderScrew(s.cx, s.cy)));
    }
    return gr;
}
// ── CORNER CONNECTOR ─────────────────────────────────────────
function renderCorner(cx, cy, rotation, highlight, exploded = false) {
    const style = getHardwareStyle(HARDWARE_LABELS.CORNER_CONNECTOR);
    const ps = 4.0, pt = 0.5;
    const gr = mkG({ transform: `translate(${cx},${cy}) rotate(${rotation})` });
    gr.appendChild(mkPath({
        d: `M0,0 L${ps},0 L${ps},${pt} L${pt},${pt} L${pt},${ps} L0,${ps} Z`,
        fill: highlight ? THEME.danger : style.color,
        stroke: style.border,
        'stroke-width': 1,
        'vector-effect': 'non-scaling-stroke',
    }));
    const sOffset = 6.0;
    if (exploded) {
        const mx = ps / 2;
        const my = ps / 2;
        // Horizontal arm screws (outside is Up/negative Y)
        const screwY = pt / 2 - sOffset;
        gr.appendChild(renderExplodeLine(mx, pt / 2, mx, screwY + 2));
        [mx - 1, mx + 1].forEach(sx => gr.appendChild(renderScrew(sx, screwY)));
        // Vertical arm screws (outside is Left/negative X)
        const screwX = pt / 2 - sOffset;
        gr.appendChild(renderExplodeLine(pt / 2, my, screwX + 2, my));
        [my - 1, my + 1].forEach(sy => gr.appendChild(renderScrew(screwX, sy)));
    }
    else {
        [0.4, 0.8].forEach(m => {
            gr.appendChild(renderScrew(ps * m, pt / 2));
            gr.appendChild(renderScrew(pt / 2, ps * m));
        });
    }
    return gr;
}
// ── SUPPORT-BAR END LOCK ─────────────────────────────────────
function renderSuppLock(x, y, highlight, vertical) {
    const style = getHardwareStyle(HARDWARE_LABELS.SUPPORT_END_CLIP);
    const w = 1.0, h = 0.6, cx = x - w / 2, cy = y - h / 2;
    const gr = mkG({ transform: `rotate(${vertical ? 90 : 0} ${x} ${y})` });
    gr.appendChild(mkRect({ x: cx, y: cy, width: w, height: h,
        fill: highlight ? THEME.danger : style.color,
        stroke: style.border,
        'stroke-width': 1,
        'vector-effect': 'non-scaling-stroke',
        rx: 0.1
    }));
    gr.appendChild(mkRect({ x: cx - .1, y: cy + h * .2, width: .1, height: h * .6, fill: '#333' }));
    gr.appendChild(mkCircle({ cx: cx + w * .7, cy: cy + h / 2, r: h * .35, fill: '#f5f5f5', stroke: '#333', 'stroke-width': 1, 'vector-effect': 'non-scaling-stroke' }));
    gr.appendChild(mkPolygon({ points: '0,-1 0.8,-0.5 0.8,0.5 0,1 -0.8,0.5 -0.8,-0.5', transform: `translate(${cx + w * .7},${cy + h / 2}) scale(${h * .15})`, fill: '#333' }));
    return gr;
}
// ── FABRIC CHANNEL CLIP ──────────────────────────────────────
function renderClip(x, y, w, h, highlight) {
    const ph = 0.2, clipY = y + h / 2 - ph / 2;
    return mkRect({ x, y: clipY, width: w, height: ph, fill: highlight ? THEME.clipFill : THEME.clipPassive, stroke: 'none', rx: 0.05 });
}
// ── WALL HANGING CLIP ────────────────────────────────────────
function renderWallClip(x, y, orientation, highlight) {
    const style = getHardwareStyle(HARDWARE_LABELS.HANGING_CLIP);
    let rot = 0;
    if (orientation === 'top')
        rot = 180;
    if (orientation === 'right')
        rot = -90;
    if (orientation === 'left')
        rot = 90;
    const s = 1.0;
    const gr = mkG({ transform: `translate(${x},${y}) rotate(${rot})` });
    gr.appendChild(mkRect({ x: -s / 2, y: -s * .3, width: s, height: s * .3, fill: HD.clipDark, stroke: '#fff', 'stroke-width': 0.5, 'vector-effect': 'non-scaling-stroke' }));
    gr.appendChild(mkRect({ x: -s / 2, y: 0, width: s, height: s,
        fill: highlight ? THEME.danger : style.color,
        stroke: style.border,
        'stroke-width': 0.5,
        'vector-effect': 'non-scaling-stroke'
    }));
    gr.appendChild(mkCircle({ cx: 0, cy: s * .5, r: s * .18, fill: '#fff', stroke: '#000', 'stroke-width': 0.5, 'vector-effect': 'non-scaling-stroke' }));
    return gr;
}
// ── CLEAT (top-rail wall mount) ──────────────────────────────
// Real-world size: 4" wide × 1" high. Centered on the segment, on the inside face.
function renderCleat(cx, yTop, highlight) {
    const style = getHardwareStyle(HARDWARE_LABELS.CLEAT);
    const cleatW = 4.0, cleatH = 1.0;
    return mkRect({
        x: cx - cleatW / 2, y: yTop, width: cleatW, height: cleatH,
        fill: highlight ? THEME.danger : style.color,
        stroke: style.border,
        'stroke-width': 1,
        'vector-effect': 'non-scaling-stroke',
        rx: 0.1,
    });
}
// ── EXPLODE LINE ─────────────────────────────────────────────
function renderExplodeLine(x1, y1, x2, y2) {
    return mkLine({
        x1, y1, x2, y2,
        stroke: THEME.accent, 'stroke-width': 1,
        'stroke-dasharray': '4,2', opacity: 0.6,
        'vector-effect': 'non-scaling-stroke',
    });
}
export function renderFrameToSvg(svg, data, opts) {
    while (svg.firstChild)
        svg.removeChild(svg.firstChild);
    if (!data)
        return;
    const { isExploded, frameColor, viewport = { zoom: 1, panX: 0, panY: 0 }, stepType = ASSEMBLY_STEP_TYPES.ORGANIZE, } = opts;
    const { w, h } = data.dims;
    const specs = data.specs;
    const strokeW = 1.5;
    const SGAP = 10.0, CGAP = 20.0, HW_EXPLODE = 8.0;
    // ── EXPLODE FLAGS ────────────────────────────────────────
    const frameExploded = isExploded;
    const gridExploded = frameExploded && stepType !== ASSEMBLY_STEP_TYPES.CORNERS;
    const GGAP = gridExploded ? 2.0 : 0.0;
    const hlSplices = stepType === ASSEMBLY_STEP_TYPES.SPLICES;
    const hlCorners = stepType === ASSEMBLY_STEP_TYPES.CORNERS;
    const hlSuppSpl = stepType === ASSEMBLY_STEP_TYPES.SUPPORTS;
    const hlClips = stepType === ASSEMBLY_STEP_TYPES.SUPPORTS;
    const hlWall = stepType === ASSEMBLY_STEP_TYPES.WALL_BRACKETS;
    const showClips = data.hw.hangingClips > 0;
    const ghostGridSteps = [
        ASSEMBLY_STEP_TYPES.ORGANIZE,
        ASSEMBLY_STEP_TYPES.SPLICES,
        ASSEMBLY_STEP_TYPES.CORNERS
    ];
    const ghostGrid = ghostGridSteps.includes(stepType);
    const showGridSteps = [
        ASSEMBLY_STEP_TYPES.SUPPORTS,
        ASSEMBLY_STEP_TYPES.WALL_BRACKETS,
        ASSEMBLY_STEP_TYPES.HANG,
        ASSEMBLY_STEP_TYPES.GRAPHIC,
        ASSEMBLY_STEP_TYPES.ORGANIZE
    ];
    const showGrid = showGridSteps.includes(stepType);
    const frameFill = frameColor === FRAME_COLORS.BLACK ? THEME.blackFill : THEME.alumLight;
    const frameStroke = frameColor === FRAME_COLORS.BLACK ? THEME.blackStroke : THEME.alumDark;
    const maxDim = Math.max(w, h);
    const vscale = Math.max(1, maxDim / 60);
    // Map Vue field names to short local names
    const tbSegs = data.frame.topBottomSegments;
    const sdSegs = data.frame.sideSegments;
    const tbLens = data.frame.topBottomLengths;
    const sdLens = data.frame.sideLengths;
    const tbStarts = data.frame.topBottomSegmentStartPositions;
    const sdStarts = data.frame.sideSegmentStartPositions;
    const vertXs = data.grid.verticalSpineXPositions;
    const rowYs = data.grid.horizontalRibYPositions;
    const vertSegs = data.grid.verticalSpineSegmentCount;
    const vertLen = data.grid.verticalSpineSegmentLength;
    // ── VIEW-BOX ─────────────────────────────────────────────
    const totalEW = w + (tbSegs - 1) * SGAP + (frameExploded ? CGAP * 2 : 0);
    const totalEH = h + (sdSegs - 1) * SGAP + (frameExploded ? CGAP * 2 : 0);
    const pad = 40 + (frameExploded ? 20 : 0);
    const vbX = -pad, vbY = -pad;
    const vbW = totalEW + pad * 2, vbH = totalEH + pad * 2;
    const dynFont = Math.max(4, maxDim / 20);
    const { zoom, panX, panY } = viewport;
    const z = Math.max(0.25, Math.min(10, zoom));
    svg.setAttribute('viewBox', `${vbX + panX} ${vbY + panY} ${vbW / z} ${vbH / z}`);
    // ── COORDINATE HELPERS ───────────────────────────────────
    const spliceShift = (i, cnt) => (!frameExploded || cnt <= 1) ? 0 : (i - (cnt - 1) / 2) * SGAP;
    const segIdx = (val, starts) => { for (let i = starts.length - 1; i >= 0; i--) {
        if (val >= starts[i])
            return i;
    } return 0; };
    const getTopX = (i) => tbStarts[i] + spliceShift(i, tbSegs);
    const getSideY = (i) => sdStarts[i] + spliceShift(i, sdSegs);
    const gvx = (x) => x + (gridExploded && tbSegs > 1 ? spliceShift(segIdx(x, tbStarts), tbSegs) : 0);
    const gvy = (y) => y + (gridExploded && sdSegs > 1 ? spliceShift(segIdx(y, sdStarts), sdSegs) : 0);
    const topYOff = frameExploded ? -CGAP : 0;
    const botYOff = h - specs.frameDepth + (frameExploded ? CGAP : 0);
    const lftXOff = frameExploded ? -CGAP : 0;
    const rgtXOff = w - specs.frameDepth + (frameExploded ? CGAP : 0);
    // ── SHADOW FILTER ────────────────────────────────────────
    svg.appendChild(makeShadowDef('seg-shadow', vscale));
    // ── DIMENSION ANNOTATIONS ────────────────────────────────
    if (!frameExploded) {
        const dg = mkG({ class: 'js-dimension-group' });
        dg.appendChild(renderDimension(0, h, w, h, `${w}"`, dynFont * 2, false, dynFont));
        dg.appendChild(renderDimension(w, 0, w, h, `${h}"`, dynFont * 2, true, dynFont));
        svg.appendChild(dg);
    }
    // ── GRID / SUPPORTS ──────────────────────────────────────
    let gridOpacity = 0; // Default: hidden
    if (frameExploded) {
        // If exploded, we want full visibility of internal parts for the diagram
        gridOpacity = 1;
    }
    else if (ghostGrid) {
        // Certain steps (like Corners) want the grid to be faint/secondary
        gridOpacity = 0.2;
    }
    else if (showGrid) {
        // Normal active visibility
        gridOpacity = 1;
    }
    else {
        // Fallback: hide grid (usually for final graphic preview steps)
        gridOpacity = 0;
    }
    const gridG = mkG({
        class: 'js-grid-group',
        opacity: gridOpacity,
    });
    vertXs.forEach((vx) => {
        const visX = gvx(vx);
        const slx = visX - specs.supportWidth / 2;
        for (let s = 0; s < vertSegs; s++) {
            const segLen = vertLen;
            const visY = gvy(specs.frameDepth + s * segLen);
            const ci = Math.min(2, segLen * 0.1);
            const spW = Math.max(0.9, specs.supportWidth * 0.35);
            const gap = Math.max(0.2, specs.supportWidth * 0.1);
            const x0 = slx + (specs.supportWidth - (spW * 2 + gap)) / 2;
            const isFirst = s === 0;
            const isLast = s === vertSegs - 1;
            const sg = mkG();
            sg.appendChild(mkRect({ x: slx, y: visY + GGAP, width: specs.supportWidth, height: Math.max(0, segLen - GGAP * 2), fill: THEME.supportFill, stroke: THEME.supportStroke, 'stroke-width': strokeW, 'vector-effect': 'non-scaling-stroke' }));
            const lockX = slx + specs.supportWidth / 2;
            if (isFirst) {
                const lockY = visY + GGAP + 0.5;
                if (gridExploded) {
                    sg.appendChild(renderExplodeLine(lockX, lockY, lockX, lockY - HW_EXPLODE));
                    sg.appendChild(renderSuppLock(lockX, lockY - HW_EXPLODE, hlSuppSpl, true));
                }
                else {
                    sg.appendChild(renderSuppLock(lockX, lockY, hlSuppSpl, true));
                }
            }
            if (isLast) {
                const lockY = visY + segLen - GGAP - 0.5;
                if (gridExploded) {
                    sg.appendChild(renderExplodeLine(lockX, lockY, lockX, lockY + HW_EXPLODE));
                    sg.appendChild(renderSuppLock(lockX, lockY + HW_EXPLODE, hlSuppSpl, true));
                }
                else {
                    sg.appendChild(renderSuppLock(lockX, lockY, hlSuppSpl, true));
                }
            }
            sg.appendChild(renderClip(slx, visY + ci, specs.supportWidth, 4, hlClips));
            sg.appendChild(renderClip(slx, visY + segLen - 4 - ci, specs.supportWidth, 4, hlClips));
            if (!isLast) {
                const sy = visY + segLen - 4 + (gridExploded ? SGAP / 2 : 0);
                if (gridExploded) {
                    // Internal Spine Splice Screws: Offset to the Left.
                    // Position two screws vertically above the junction and two below it.
                    const sOffset = 6.0;
                    const screwX = x0 - sOffset;
                    const screwY_top = sy;
                    const screwY_bot = sy + 8;
                    // Projection lines
                    sg.appendChild(renderExplodeLine(screwX + 2, screwY_top, x0 + spW + gap / 2, screwY_top));
                    sg.appendChild(renderExplodeLine(screwX + 2, screwY_bot, x0 + spW + gap / 2, screwY_bot));
                    // 2 screws above junction
                    sg.appendChild(renderScrew(screwX, screwY_top - 1));
                    sg.appendChild(renderScrew(screwX, screwY_top - 3.5));
                    // 2 screws below junction
                    sg.appendChild(renderScrew(screwX, screwY_bot + 1));
                    sg.appendChild(renderScrew(screwX, screwY_bot + 3.5));
                    sg.appendChild(renderSplice(x0, sy, spW, 8, hlSuppSpl, true, false));
                    sg.appendChild(renderSplice(x0 + spW + gap, sy, spW, 8, hlSuppSpl, true, false));
                }
                else {
                    sg.appendChild(renderSplice(x0, sy, spW, 8, hlSuppSpl, true));
                    sg.appendChild(renderSplice(x0 + spW + gap, sy, spW, 8, hlSuppSpl, true));
                }
            }
            gridG.appendChild(sg);
        }
    });
    rowYs.forEach((ry) => {
        const vy2 = gvy(ry);
        const fdv = gvx(specs.frameDepth);
        const fwv = gvx(w - specs.frameDepth);
        const drawRib = (sx, rw) => {
            const rg = mkG();
            rg.appendChild(mkRect({ x: sx, y: vy2, width: rw, height: specs.supportWidth, fill: THEME.supportFill, stroke: THEME.supportStroke, 'stroke-width': strokeW, 'vector-effect': 'non-scaling-stroke' }));
            const lockY = vy2 + specs.supportWidth / 2;
            const leftLockX = sx + 0.5;
            const rightLockX = sx + rw - 0.5;
            if (gridExploded) {
                rg.appendChild(renderExplodeLine(leftLockX, lockY, leftLockX - HW_EXPLODE, lockY));
                rg.appendChild(renderSuppLock(leftLockX - HW_EXPLODE, lockY, hlSuppSpl, false));
                rg.appendChild(renderExplodeLine(rightLockX, lockY, rightLockX + HW_EXPLODE, lockY));
                rg.appendChild(renderSuppLock(rightLockX + HW_EXPLODE, lockY, hlSuppSpl, false));
            }
            else {
                rg.appendChild(renderSuppLock(leftLockX, lockY, hlSuppSpl, false));
                rg.appendChild(renderSuppLock(rightLockX, lockY, hlSuppSpl, false));
            }
            return rg;
        };
        const rg = mkG();
        if (vertXs.length === 0) {
            rg.appendChild(drawRib(fdv + GGAP, Math.max(0, fwv - fdv - GGAP * 2)));
        }
        else {
            const v0v = gvx(vertXs[0]);
            rg.appendChild(drawRib(fdv + GGAP, Math.max(0, v0v - fdv - specs.supportWidth / 2 - GGAP * 2)));
            vertXs.forEach((v1, i) => {
                if (i === vertXs.length - 1)
                    return;
                const v1v = gvx(v1), v2v = gvx(vertXs[i + 1]);
                rg.appendChild(drawRib(v1v + specs.supportWidth / 2 + GGAP, Math.max(0, v2v - v1v - specs.supportWidth - GGAP * 2)));
            });
            const lvv = gvx(vertXs[vertXs.length - 1]);
            rg.appendChild(drawRib(lvv + specs.supportWidth / 2 + GGAP, Math.max(0, fwv - lvv - specs.supportWidth / 2 - GGAP * 2)));
        }
        gridG.appendChild(rg);
    });
    svg.appendChild(gridG);
    // ── FRAME RAILS ──────────────────────────────────────────
    const frameG = mkG({ class: 'js-frame-group' });
    // TOP
    const topG = mkG({ transform: `translate(0 ${topYOff})` });
    for (let i = 0; i < tbSegs; i++) {
        const sl = tbLens[i], vx3 = getTopX(i);
        const mt = tbSegs === 1 ? 'top-full' : i === 0 ? 'top-start' : i === tbSegs - 1 ? 'top-end' : 'straight';
        topG.appendChild(renderFrameSeg(vx3, 0, sl, specs.frameDepth, mt, frameFill, frameStroke, strokeW, 'seg-shadow'));
        if (showClips)
            (data.frame.clipsTopBottom[i] || []).forEach(pos => {
                const clipX = vx3 + pos;
                if (frameExploded) {
                    topG.appendChild(renderExplodeLine(clipX, specs.frameDepth, clipX, specs.frameDepth + HW_EXPLODE));
                    topG.appendChild(renderWallClip(clipX, specs.frameDepth + HW_EXPLODE, 'bottom', hlWall));
                }
                else {
                    topG.appendChild(renderWallClip(clipX, specs.frameDepth, 'bottom', hlWall));
                }
            });
        // Cleat — 1 per top rail segment, centered, on the inside (frame-center) face
        if (data.hw.cleats > 0) {
            const segCenterX = vx3 + sl / 2;
            if (frameExploded) {
                topG.appendChild(renderExplodeLine(segCenterX, specs.frameDepth, segCenterX, specs.frameDepth + HW_EXPLODE));
                topG.appendChild(renderCleat(segCenterX, specs.frameDepth + HW_EXPLODE, hlWall));
            }
            else {
                topG.appendChild(renderCleat(segCenterX, specs.frameDepth, hlWall));
            }
        }
    }
    if (frameExploded) {
        for (let i = 0; i < tbSegs - 1; i++) {
            const xl = getTopX(i) + tbLens[i], xr = getTopX(i + 1), mx = (xl + xr) / 2;
            const yp = specs.frameDepth / 2;
            const sOffset = 6.0;
            topG.appendChild(renderExplodeLine(xl, yp, xr, yp));
            // Offset 'outside' (Up for Top rail)
            const screwY = yp - sOffset;
            topG.appendChild(renderExplodeLine(mx, yp, mx, screwY + 2));
            [mx - 3, mx - 1, mx + 1, mx + 3].forEach(sx => topG.appendChild(renderScrew(sx, screwY)));
            topG.appendChild(renderSplice(mx - 4, yp - .25, 8, .5, hlSplices, false, false));
        }
    }
    frameG.appendChild(topG);
    // BOTTOM
    const botG = mkG({ transform: `translate(0 ${botYOff})` });
    for (let i = 0; i < tbSegs; i++) {
        const sl = tbLens[i], vx3 = getTopX(i);
        const mt = tbSegs === 1 ? 'bot-full' : i === 0 ? 'bot-start' : i === tbSegs - 1 ? 'bot-end' : 'straight';
        botG.appendChild(renderFrameSeg(vx3, 0, sl, specs.frameDepth, mt, frameFill, frameStroke, strokeW, 'seg-shadow'));
        if (showClips)
            (data.frame.clipsTopBottom[i] || []).forEach(pos => {
                const clipX = vx3 + pos;
                if (frameExploded) {
                    botG.appendChild(renderExplodeLine(clipX, 0, clipX, -HW_EXPLODE));
                    botG.appendChild(renderWallClip(clipX, -HW_EXPLODE, 'top', hlWall));
                }
                else {
                    botG.appendChild(renderWallClip(clipX, 0, 'top', hlWall));
                }
            });
    }
    if (frameExploded) {
        for (let i = 0; i < tbSegs - 1; i++) {
            const xl = getTopX(i) + tbLens[i], xr = getTopX(i + 1), mx = (xl + xr) / 2, yp = specs.frameDepth / 2;
            const sOffset = 6.0;
            botG.appendChild(renderExplodeLine(xl, yp, xr, yp));
            // Offset 'outside' (Down for Bottom rail)
            const screwY = yp + sOffset;
            botG.appendChild(renderExplodeLine(mx, yp, mx, screwY - 2));
            [mx - 3, mx - 1, mx + 1, mx + 3].forEach(sx => botG.appendChild(renderScrew(sx, screwY)));
            botG.appendChild(renderSplice(mx - 4, yp - .25, 8, .5, hlSplices, false, false));
        }
    }
    frameG.appendChild(botG);
    // LEFT
    const lftG = mkG({ transform: `translate(${lftXOff} 0)` });
    for (let i = 0; i < sdSegs; i++) {
        const sl = sdLens[i], vy3 = getSideY(i);
        const mt = sdSegs === 1 ? 'left-full' : i === 0 ? 'left-start' : i === sdSegs - 1 ? 'left-end' : 'straight';
        lftG.appendChild(renderFrameSeg(0, vy3, specs.frameDepth, sl, mt, frameFill, frameStroke, strokeW, 'seg-shadow'));
        if (showClips)
            (data.frame.clipsLeftRight[i] || []).forEach(pos => {
                const clipY = vy3 + pos;
                if (frameExploded) {
                    lftG.appendChild(renderExplodeLine(specs.frameDepth, clipY, specs.frameDepth + HW_EXPLODE, clipY));
                    lftG.appendChild(renderWallClip(specs.frameDepth + HW_EXPLODE, clipY, 'right', hlWall));
                }
                else {
                    lftG.appendChild(renderWallClip(specs.frameDepth, clipY, 'right', hlWall));
                }
            });
    }
    if (frameExploded) {
        for (let i = 0; i < sdSegs - 1; i++) {
            const yt = getSideY(i) + sdLens[i], yb = getSideY(i + 1), my = (yt + yb) / 2, xp = specs.frameDepth / 2;
            const sOffset = 6.0;
            lftG.appendChild(renderExplodeLine(xp, yt, xp, yb));
            // Offset 'outside' (Left for Left rail)
            const screwX = xp - sOffset;
            lftG.appendChild(renderExplodeLine(xp, my, screwX + 2, my));
            [my - 3, my - 1, my + 1, my + 3].forEach(sy => lftG.appendChild(renderScrew(screwX, sy)));
            lftG.appendChild(renderSplice(xp - .25, my - 4, .5, 8, hlSplices, true, false));
        }
    }
    frameG.appendChild(lftG);
    // RIGHT
    const rgtG = mkG({ transform: `translate(${rgtXOff} 0)` });
    for (let i = 0; i < sdSegs; i++) {
        const sl = sdLens[i], vy3 = getSideY(i);
        const mt = sdSegs === 1 ? 'right-full' : i === 0 ? 'right-start' : i === sdSegs - 1 ? 'right-end' : 'straight';
        rgtG.appendChild(renderFrameSeg(0, vy3, specs.frameDepth, sl, mt, frameFill, frameStroke, strokeW, 'seg-shadow'));
        if (showClips)
            (data.frame.clipsLeftRight[i] || []).forEach(pos => {
                const clipY = vy3 + pos;
                if (frameExploded) {
                    rgtG.appendChild(renderExplodeLine(0, clipY, -HW_EXPLODE, clipY));
                    rgtG.appendChild(renderWallClip(-HW_EXPLODE, clipY, 'left', hlWall));
                }
                else {
                    rgtG.appendChild(renderWallClip(0, clipY, 'left', hlWall));
                }
            });
    }
    if (frameExploded) {
        for (let i = 0; i < sdSegs - 1; i++) {
            const yt = getSideY(i) + sdLens[i], yb = getSideY(i + 1), my = (yt + yb) / 2, xp = specs.frameDepth / 2;
            const sOffset = 6.0;
            rgtG.appendChild(renderExplodeLine(xp, yt, xp, yb));
            // Offset 'outside' (Right for Right rail)
            const screwX = xp + sOffset;
            rgtG.appendChild(renderExplodeLine(xp, my, screwX - 2, my));
            [my - 3, my - 1, my + 1, my + 3].forEach(sy => rgtG.appendChild(renderScrew(screwX, sy)));
            rgtG.appendChild(renderSplice(xp - .25, my - 4, .5, 8, hlSplices, true, false));
        }
    }
    frameG.appendChild(rgtG);
    // CORNER CONNECTORS (explode view)
    if (frameExploded) {
        const cg = mkG({ class: 'js-corner-connectors' });
        const tlX = -CGAP, tlY = -CGAP, trX = w + CGAP, trY = -CGAP, brX = w + CGAP, brY = h + CGAP, blX = -CGAP, blY = h + CGAP;
        cg.appendChild(renderExplodeLine(0, 0, tlX, tlY));
        cg.appendChild(renderCorner(tlX - 10, tlY - 10, 0, hlCorners, true));
        cg.appendChild(renderExplodeLine(w, 0, trX, trY));
        cg.appendChild(renderCorner(trX + 10, trY - 10, 90, hlCorners, true));
        cg.appendChild(renderExplodeLine(w, h, brX, brY));
        cg.appendChild(renderCorner(brX + 10, brY + 10, 180, hlCorners, true));
        cg.appendChild(renderExplodeLine(0, h, blX, blY));
        cg.appendChild(renderCorner(blX - 10, blY + 10, 270, hlCorners, true));
        frameG.appendChild(cg);
    }
    svg.appendChild(frameG);
}
/**
 * Retrieves the style object from the legend based on the label.
 */
function getHardwareStyle(label) {
    const item = HARDWARE_LEGEND.find(l => l.label === label);
    return item || { color: '#cccccc', border: '#333333' }; // Fallback
}
export const trimSvg = (svgRefValue, padding = 10) => {
    const svg = svgRefValue;
    if (!svg)
        return;
    try {
        // Get the bounding box of the generated content
        const bbox = svg.getBBox();
        if (bbox.width > 0 && bbox.height > 0) {
            // Set the viewBox based on the content's actual dimensions
            const x = bbox.x - padding;
            const y = bbox.y - padding;
            const width = bbox.width + (padding * 2);
            const height = bbox.height + (padding * 2);
            svg.setAttribute('viewBox', `${x} ${y} ${width} ${height}`);
        }
    }
    catch (error) {
        // silent fail
    }
};
